import * as Objdict from "objdict"
import Poin from "poin"
import Geometry from "@frog/source/utility/Geometry.js"

let renderables = []

import {performer, loader} from "./gl/render.js"
import * as events from "./events.js"

export default new class Canvas {
    constructor() {
        this.size = {"width": 0, "height": 0},
        this.preload = []
        this.defaultAnchor = {"x": 0, "y": 0}
        this.pointer = undefined
    }
    start(settings) {
        Object.keys(settings).forEach((key) => {
            this[key] = settings[key]
        })

        performer.start(this)
        loader.start(settings.preload)
        events.start()
    }
    render(view) {
        view = view || this.view

        this.previousState = this.nextState || {}
        this.nextState = {}
        this.pointerRelativePosition = this.calculateRelativePosition(Poin.position)

        // process
        renderables = view()
        if(renderables == undefined) return
        renderables = this.calculateProperties(renderables)
        renderables = this.calculateSizes(renderables)
        renderables = this.calculatePositions(renderables) // TODO: DO THIS!!
        renderables = this.calculateState(renderables)
        window.renderables = renderables

        // perform
        performer.clear()
        this.performRender(renderables)

        // events
        if(renderables != undefined) {
            events.handleEvents(renderables)
        }
    }
    // COPIED FROM MODELS/SELECTION.JS
    calculateRelativePosition({x, y}) {
        if(this.dom == undefined) return {"x": 0, "y": 0}
        const bounds = this.dom.getBoundingClientRect()
        x = ((x - bounds.left) / bounds.width) * this.size.width
        y = ((y - bounds.top) / bounds.height) * this.size.height
        return {x, y}
    }
    performRender(renderable) {
        performer.render(renderable)

        if(renderable.children != undefined) {
            Object.values(renderable.children).sort((a, b) => {
                return (a.stack || a.position?.stack || 0) - (b.stack || b.position?.stack ||0)
            }).forEach((subrenderable) => {
                this.performRender(subrenderable)
            })

            // Note! Can only sort siblings; does not handle z-stacking of cousins.
            // If the parent does not have a stack, it is assumed at 0, regardless
            // of that parent's children.
        }
    }

    ///////////////////////////////
    // CONVERTING FOR RENDERING //
    /////////////////////////////

    calculateProperties(view) {
        if(view == undefined) return

        ////////////////
        // OVERRIDES //
        //////////////

        if(view.overrides != undefined) {
            Object.keys(view.overrides).forEach((overrideKey) => {
                // an overrideKey could be hovered or pressed, for example
                if(this.previousState != undefined
                && this.previousState[view.key] != undefined
                && this.previousState[view.key][overrideKey] != undefined) {
                    const overridingView = view.overrides[overrideKey]
                    Object.keys(overridingView).forEach((key) => {
                        view[key] = overridingView[key]
                    })
                }
            })
        }

        /////////////////
        // INSTANCING //
        ///////////////

        view.parent = view.parent || {
            "key": "Root",
            "children": [view],
            "width": this.size.width,
            "height": this.size.height,
            "bounds": {
                "x1": 0,
                "y1": 0,
                "x2": this.size.width,
                "y2": this.size.height,
                "width": this.size.width,
                "height": this.size.height
            },
        }

        // Support aliases.
        if(view._ != undefined) {
            view.children = view.children || view._
            delete view._
        }
        if(view.children != undefined) {
            // Support array as new siblings.
            view.children = view.children.reduce((children, child) => {
                // TODO: We're only supporting 2 generations of arrays of arrays.
                if(child instanceof Array) {
                    child = child.reduce((array, grandchild) => {
                        return array.concat(grandchild)
                    }, [])
                }
                return children.concat(child)
            }, [])

            // Filter out falsy views.
            view.children = view.children.filter((child) => {
                return child != undefined
                    && child != false
                    && child != null
                    && child.ignore != true
            })
        }

        ///////////////
        // GRAPHICS //
        /////////////

        // Support different color formats.
        try {
            if(view.color != undefined) {
                view.color = translateColor(view.color)
            }
            if(view.outline != undefined) {
                view.outline.color = translateColor(view.outline.color)
            }
            if(view.tint != undefined) {
                view.tint.color = translateColor(view.tint.color)
            }
        } catch(error) {
            console.log(error)
            console.log(view)
        }

        view.opacity = view.opacity ?? 1
        if(view.children != undefined) {
            view.children.forEach((child) => {
                child.opacity = child.opacity ?? 1
                child.opacity = view.opacity * child.opacity
            })
        }

        /////////////
        // BOUNDS //
        ///////////

        // Support aliases.
        ;["x", "y", "x1", "y1", "x2", "y2", "left", "right", "top", "bottom"].forEach((key) => {
            if(view[key] != undefined) {
                view.position = view.position || {}
                view.position[key] = view.position[key] || view[key]
                delete view[key]
            }
        })

        if(view.image != undefined) {
            if(loader.images[view.image] != undefined
            && loader.images[view.image].isLoaded == true) {
                view.imagewidth = loader.images[view.image].width
                view.imageheight = loader.images[view.image].height
            }
        }

        if(view.width != undefined) {
            view.width = translateSpace(view.width, view.parent.imagewidth ?? view.parent.width ?? 0) // PERCENTAGES DON'T WORK ON
        }
        if(view.height != undefined) {
            view.height = translateSpace(view.height, view.parent.imageheight ?? view.parent.height ?? 0)
        }

        ///////////////
        // CHILDREN //
        /////////////

        if(view.children != undefined) {
            view.children.forEach((child, index) => {
                // Default and extend the key.
                child.key = child.key || index
                child.key = view.key + "/" + child.key

                // Link the child to the parent.
                child.parent = view

                // Flow to all children.
                this.calculateProperties(child)
            })
        }

        ///////////
        // DONE //
        /////////

        return view
    }

    // adds isFlex, padding
    calculateSizes(view) {
        if(view == undefined) return

        ///////////////
        // CHILDREN //
        /////////////

        // Flow to all children.
        if(view.children != undefined) {
            view.children.forEach((child) => {
                this.calculateSizes(child)
            })
        }

        /////////////////////
        // WIDTH & HEIGHT //
        ///////////////////

        // // Calculate the size if you have
        // // any children without a position.
        // if(view.children != undefined
        // && view.childflow != undefined) {
        //     // view.children.forEach((childview) => {
        //     //     if(childview.position == undefined) {
        //     //         view.flexwidth = Math.max(view.flexwidth ?? -Infinity, childview.truewidth)
        //     //         view.flexheight = Math.max(view.flexheight ?? -Infinity, childview.trueheight)
        //     //     }
        //     // })
        // }

        view.truewidth = view.width ?? view.imagewidth ?? view.childflow?.width ?? 0
        view.trueheight = view.height ?? view.imageheight ?? view.childflow?.height ?? 0

        ///////////////
        // Siblings //
        /////////////

        if(view.position == undefined
        && view.parent != undefined) {
            view.parent.childflow = view.parent.childflow || {}
            view.parent.childflow.x = view.parent.childflow.x || 0
            view.parent.childflow.y = view.parent.childflow.y || 0

            view.flow = view.flow || {}
            view.flow.x = view.parent.childflow.x
            view.flow.y = view.parent.childflow.y

            view.parent.childflow.x = view.flow.x + view.truewidth + (view["margin-right"] || 0)
            view.parent.childflow.y = view.flow.y

            view.parent.childflow.width = Math.max(view.flow.x + view.truewidth, view.parent.childflow.width || 0)
            view.parent.childflow.height = Math.max(view.flow.y + view.trueheight, view.parent.childflow.height || 0)
            // HEIGHT IS WRONG
        }

        // if(view.padding != undefined) {
        //     if(isNaN(view.padding) == false) {
        //         view.padding = {
        //             "left": view.padding,
        //             "right": view.padding,
        //             "top": view.padding,
        //             "bottom": view.padding,
        //         }
        //     }
        //     view.bounds.x2 += view.padding.left + view.padding.right
        //     view.bounds.y2 += view.padding.top + view.padding.bottom
        //     if(view.children != undefined) {
        //         view.children.forEach((childview) => {
        //             childview.bounds.x1 += view.padding.left
        //             childview.bounds.x2 += view.padding.left
        //             childview.bounds.y1 += view.padding.top
        //             childview.bounds.y2 += view.padding.top
        //         })
        //     }
        // }

        ///////////
        // DONE //
        /////////

        return view
    }

    // This operation will take a renderable
    // and mutate values that are relative or
    // unatomic into pure render-ready data.
    // Positions and dimensions will be in
    // pixels and stored in bounds, colors
    // will be in arrays. Will also iterate
    // through all children. It will add
    // a parent to the top-level renderable.
    // It will key everything relative to
    // the family tree.
    calculatePositions(view) {
        if(view == undefined) return

        if(view.position != undefined) {
            // Support % and px and other string-formatted values.
            ;["left", "right", "x", "x1", "x2"].forEach((key) => {
                if(view.position[key] != undefined) {
                    view.position[key] = translateSpace(view.position[key], view.parent.bounds.width)
                }
            })
            ;["top", "bottom", "y", "y1", "y2"].forEach((key) => {
                if(view.position[key] != undefined) {
                    view.position[key] = translateSpace(view.position[key], view.parent.bounds.height)
                }
            })
        }

        /////////////
        // BOUNDS //
        ///////////

        // Position relative to their parent.
        // if(view.parent.bounds.x1 != undefined) {
        //     ["x", "x1", "x2", "left"].forEach((key) => {
        //         if(view[key] != undefined) {
        //             view[key] = view.parent.bounds.x1 + view[key]
        //         }
        //     })
        // }
        // if(view.parent.bounds.y1 != undefined) {
        //     ["y", "y1", "y2", "top"].forEach((key) => {
        //         if(view[key] != undefined) {
        //             view[key] = view.parent.bounds.y1 + view[key]
        //         }
        //     })
        // }
        // if(view.parent.bounds.x2 != undefined) {
        //     ["right"].forEach((key) => {
        //         if(view[key] != undefined) {
        //             view[key] = view.parent.bounds.x2 - view[key]
        //         }
        //     })
        // }
        // if(view.parent.bounds.y2 != undefined) {
        //     ["bottom"].forEach((key) => {
        //         if(view[key] != undefined) {
        //             view[key] = view.parent.bounds.y2 - view[key]
        //         }
        //     })
        // }

        view.bounds = view.bounds || {}

        // Position relative to the parent.
        view.bounds.x1 = view.parent.bounds.x1
        view.bounds.x2 = view.parent.bounds.x1
        view.bounds.y1 = view.parent.bounds.y1
        view.bounds.y2 = view.parent.bounds.y1

        // Width and Height
        view.bounds.x2 += view.truewidth
        view.bounds.y2 += view.trueheight

        // Position
        if(view.position != undefined) {
            if(view.position.x != undefined) {
                view.bounds.x1 += view.position.x
                view.bounds.x2 += view.position.x
            }
            if(view.position.y != undefined) {
                view.bounds.y1 += view.position.y
                view.bounds.y2 += view.position.y
            }
        }
        if(view.flow != undefined) {
            view.bounds.x1 += view.flow.x
            view.bounds.x2 += view.flow.x
            view.bounds.y1 += view.flow.y
            view.bounds.y2 += view.flow.y
        }

        // view.bounds = view.bounds ?? {}
        // view.bounds.x1 = view.x1 ?? view.x ?? view.left
        // view.bounds.y1 = view.y1 ?? view.y ?? view.top
        // view.bounds.x2 = view.x2 ?? view.right
        // view.bounds.y2 = view.y2 ?? view.bottom

        // // Calculating bound using size and opposite bound.
        // if(view.bounds.x1 == undefined && view.bounds.x2 != undefined && (view.width ?? view.imagewidth) != undefined) {
        //     view.bounds.x1 = view.bounds.x2 - (view.width ?? view.imagewidth)
        // }
        // if(view.bounds.y1 == undefined && view.bounds.y2 != undefined && (view.height ?? view.imageheight) != undefined) {
        //     view.bounds.y1 = view.bounds.y2 - (view.height ?? view.imageheight)
        // }
        //
        // // Default the bounds.
        // view.bounds.x1 = view.bounds.x1 ?? view.parent.bounds.x1 ?? 0
        // view.bounds.y1 = view.bounds.y1 ?? view.parent.bounds.y1 ?? 0
        //
        // // Calculating bound using size and opposite bound.
        // if(view.bounds.x1 != undefined && view.bounds.x2 == undefined && (view.width ?? view.imagewidth) != undefined) {
        //     view.bounds.x2 = view.bounds.x1 + (view.width ?? view.imagewidth)
        // }
        // if(view.bounds.y1 != undefined && view.bounds.y2 == undefined && (view.height ?? view.imageheight) != undefined) {
        //     view.bounds.y2 = view.bounds.y1 + (view.height ?? view.imageheight)
        // }
        //
        // // Default the bounds
        // view.bounds.x2 = view.bounds.x2 ?? view.bounds.x1 ?? 0
        // view.bounds.y2 = view.bounds.y2 ?? view.bounds.y1 ?? 0

        view.bounds.width = view.bounds.x2 - view.bounds.x1
        view.bounds.height = view.bounds.y2 - view.bounds.y1

        /////////////
        // ANCHOR //
        ///////////

        if(view.anchor != undefined) {
            view.anchor.x = translateSpace(view.anchor.x, view.bounds.width) || 0
            view.anchor.y = translateSpace(view.anchor.y, view.bounds.height) || 0

            view.bounds.x1 -= view.anchor.x
            view.bounds.x2 -= view.anchor.x
            view.bounds.y1 -= view.anchor.y
            view.bounds.y2 -= view.anchor.y
        }

        ///////////////
        // CHILDREN //
        /////////////

        // Iterate over the children.
        if(view.children instanceof Array) {
            view.children.forEach((child) => {
                this.calculatePositions(child)
            })
        }

        ///////////
        // DONE //
        /////////

        return view
    }

    calculateState(view) {
        this.nextState[view.key] = {}

        // hovered
        if(Geometry.isBounded(view.bounds, this.pointerRelativePosition)) {
            this.nextState[view.key]["hovered"] = true

            if(Poin.isPressed()) {
                this.nextState[view.key]["pressed"] = true
            }
        }

        ///////////////
        // CHILDREN //
        /////////////

        // Iterate over the children.
        if(view.children instanceof Array) {
            view.children.forEach((child) => {
                this.calculateState(child)
            })
        }

        ///////////
        // DONE //
        /////////

        return view
    }
}

function translateSpace(value, parentvalue) {
    if(value == undefined) return undefined
    value = value || 0

    if(isNaN(value)) {
        if(value.endsWith("%")) {
            value = parseFloat(value) / 100
            if(parentvalue != undefined) {
                value *= parentvalue
            }
        } else if(value.endsWith("px")) {
            value = Math.round(parseFloat(value))
        }
    }

    return value
}

import translateColor from "@frog/source/juice/utility/translateColor.js"

// TODO: CAN I RESIZE THE IMAGES IF I GIVE MY OWN WIDTH HEIGHT

// function print(renderable) {
//     if(renderable == undefined) return
//
//     const rerenderable = {}
//     rerenderable["key"] = renderable["key"]
//     Object.keys(renderable).sort().forEach((key) => {
//         rerenderable[key] = renderable[key]
//     })
//
//     console.group(rerenderable)
//     if(renderable.children != undefined) {
//         Objdict.forEach(renderable.children, (child) => {
//             printRenderables(child)
//         })
//     }
//     console.groupEnd()
// }

// top
// left
// down
// bottom
// x
// y
// x1
// x2
// y1
// y2
// position.x
// position.y
// If you use both top/left/bottom/right and x1/x2/y1/y2 and x/y, it'll prioritize
// some and ignore the others. You can provide these both percentage (relative to
// the parent's width and height) or a pixel.
// If x1 and x2 or left and right or top and bottom overlap, you'll get a view
// that stretches beyond the breached edge.
// note well that bottom and right do not function like x2 and y2. bottom and right
// are relative to the bottom and right edges of the parent, while x2 and y2 are
// relative to the top-left corner, just like x1 and y1.
//
// anchor.x
// anchor.y
// The anchors take x1/y1 as the original anchor, and then shift everything else
// to make your new "anchor" that position. This can be a percentage relative
// to the size of the view, or a pixel amount.
// If you combine this with the left/right/bottom/top fields, anchor will use
// those fields to size the view, and then just shift everything in the top/left
// to fit the provided anchor.
//
// color
// Accepts #FFFFFF, 0xFFFFFF or [1, 1, 1]
// Works with images, as background color.
//
// events.cursordown
//
// image
// must be the source string, not the image dom or image require module.
// when given an image, it'll also bring it's width and height.
//
// children or _
// key
// type (is deprecated now)
//
// opacity
// isFlipped (image only?)
// grayscale (image only)
// tint (image only)
// outline (image only)

// // copied from render.js
// {
//     "position": {"x": 0, "y": 0}, // defaulted to 0
//     // "width": 8,
//     // "height": 8, // if width or height is left off, it'll use the default px width or height of the original image.
//     "image": "url", // if it isn't loaded, it'll load it. if it can't find it, it'll ignore(?). if this is undefined, it'll error.
//     "tint": {"color": 0xFFFFFF, "intensity": 1},
//     "outline": {"color": "#FFFFFF", "thickness": 1}, // i don't think thickness is supported yet.
//     "grayscale": ...,
//     "isFlipped": true,
//     "opacity": 0.5,
// }
// {
//     "position": {"x": 0, "y:" 0} // if left off, skips.
//     "color": 0xFFFFFF, // "#FFFFFF". if left off, defaults to hot pink. By this render stage, it should be just the array, but it can be transposed from 0xFFFFFF or #FFFFFF formats.
//     "opacity": 1, // if left off, defaults to 1.
//     "width": 8, // if left off, skips.
//     "height": 8, // if left off, skips.
// }

// // This should be calculated during the conversion. During the rendering, it is assumed the anchor is the top left 0x0.
// "anchor": {"x": 0, "y": 0}, // if x or y is left of, defaulted to 0. if left off entirely, it checks the Canvas.defaultAnchor
// // This should honestly just be guessed at this point.
// "type": "sprite", // if type is left off, it assumes sprite.
